import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Truck, Globe, ExternalLink, Phone, MapPin, Package } from "lucide-react";

const UBER_URL = "https://www.uber.com/mx/es/business/direct/";

export const EnviosTab = ({ tiendaId, subOrdenes, onRefresh }) => {
  const [tienda, setTienda] = useState(null);

  useEffect(() => { api.get(`/tiendas/${tiendaId}`).then(r => setTienda(r.data)); }, [tiendaId]);

  const toggle = async (campo, valor) => {
    try {
      const { data } = await api.patch(`/tiendas/${tiendaId}/perfil`, { [campo]: valor });
      setTienda(data.tienda);
      toast.success(valor ? "Opción activada" : "Opción desactivada");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const foraneos = subOrdenes.filter(s => s.metodo_envio === "foraneo");
  const uber = subOrdenes.filter(s => s.metodo_envio === "uber");

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 rounded-sm p-5 flex gap-4">
          <div className="w-10 h-10 rounded-sm bg-zinc-900 text-white grid place-items-center shrink-0"><Truck className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <div className="font-display font-bold tracking-tight">Enviar productos por medio de Uber</div>
              <Switch data-testid="toggle-uber" checked={!!tienda?.envia_uber} onCheckedChange={v => toggle("envia_uber", v)} />
            </div>
            <p className="text-xs text-zinc-500 mt-1">Tus clientes verán la opción "Recibir mi pedido por Uber". Tú solicitas el viaje desde la plataforma de Uber.</p>
            <a href={UBER_URL} target="_blank" rel="noreferrer" data-testid="uber-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-zinc-900 underline underline-offset-4 hover:text-[#FF3366]">
              Abrir Uber Direct (entregas) <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-5 flex gap-4">
          <div className="w-10 h-10 rounded-sm bg-[#D4FF00] grid place-items-center shrink-0"><Globe className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <div className="font-display font-bold tracking-tight">Envíos foráneos a otras poblaciones</div>
              <Switch data-testid="toggle-foraneo" checked={!!tienda?.envia_foraneo} onCheckedChange={v => toggle("envia_foraneo", v)} />
            </div>
            <p className="text-xs text-zinc-500 mt-1">En envíos foráneos el tiempo de entrega es hasta en 72 horas. Tú eliges la paquetería y coordinas el costo con el cliente.</p>
          </div>
        </div>
      </div>

      <section>
        <div className="overline text-zinc-500 mb-3">Pedidos foráneos ({foraneos.length})</div>
        {foraneos.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-10 text-center text-zinc-400 text-sm">Aún no tienes pedidos foráneos</div>
        ) : (
          <div className="grid gap-3">{foraneos.map(s => <PedidoEnvioCard key={s.id} sub={s} onRefresh={onRefresh} />)}</div>
        )}
      </section>

      {uber.length > 0 && (
        <section>
          <div className="overline text-zinc-500 mb-3">Pedidos por Uber ({uber.length})</div>
          <div className="grid gap-3">{uber.map(s => <PedidoEnvioCard key={s.id} sub={s} onRefresh={onRefresh} />)}</div>
        </section>
      )}
    </div>
  );
};

const SIGUIENTE = { Pendiente: null, Preparando: ["Empacado", "Marcar Empacado"], Empacado: ["En_Ruta", "Marcar Enviado"], Listo: ["En_Ruta", "Marcar Enviado"], En_Ruta: ["Entregado", "Marcar Entregado"], Entregado: null };

function PedidoEnvioCard({ sub, onRefresh }) {
  const d = sub.datos_foraneo || {};
  const pagado = sub.payment_status === "paid";
  const next = SIGUIENTE[sub.estado];
  const avanzar = async () => {
    try {
      await api.patch(`/sub-ordenes/${sub.id}/estado`, { estado: next[0] });
      toast.success("Estado actualizado"); onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid={`foraneo-card-${sub.id}`} className="bg-white border border-zinc-200 rounded-sm p-5 grid md:grid-cols-[1fr_280px] gap-5">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`border-0 ${pagado ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{pagado ? "Pagado" : "Pago pendiente"}</Badge>
          <Badge className="bg-zinc-100 text-zinc-700 border-0">{sub.estado}</Badge>
          <Badge className="bg-[#D4FF00] text-zinc-900 border-0">{sub.metodo_envio === "uber" ? "Uber" : "Foráneo · hasta 72 h"}</Badge>
          <span className="text-xs text-zinc-400 ml-auto">{new Date(sub.created_at).toLocaleString()}</span>
        </div>
        <div className="mt-3 text-sm divide-y divide-zinc-100">
          {sub.items.map((it, i) => (
            <div key={`${sub.id}-${i}`} className="flex justify-between py-1.5">
              <span className="flex items-center gap-2"><Package className="w-3.5 h-3.5 text-zinc-400" /> {it.cantidad}× {it.nombre}</span>
              <span className="text-zinc-500">${it.precio_unitario.toFixed(2)} · <b className="text-zinc-900">${it.subtotal.toFixed(2)}</b></span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex justify-between items-center border-t border-zinc-200 pt-3">
          <span className="text-xs uppercase tracking-wider text-zinc-500">Total a cobrar</span>
          <span className="font-display font-black text-xl" data-testid={`foraneo-total-${sub.id}`}>${sub.total.toFixed(2)}</span>
        </div>
        {next && pagado && (
          <Button size="sm" data-testid={`foraneo-avanzar-${sub.id}`} onClick={avanzar} className="mt-3 rounded-sm bg-zinc-900 hover:bg-[#FF3366]">{next[1]}</Button>
        )}
      </div>
      <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 text-sm">
        <div className="overline text-zinc-500 mb-2">Datos del cliente</div>
        <div className="font-display font-bold">{d.nombre || sub.cliente_nombre}</div>
        <div className="flex items-center gap-1.5 text-zinc-600 mt-1"><Phone className="w-3.5 h-3.5" /> {d.telefono || sub.cliente_telefono || "—"}</div>
        <div className="flex items-start gap-1.5 text-zinc-600 mt-1"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{sub.direccion_entrega || "—"}</span></div>
        {d.referencias && <div className="text-xs text-zinc-500 mt-2">Ref: {d.referencias}</div>}
      </div>
    </div>
  );
}
